import { useEffectOnUpdate } from '@old/hooks/core';
import {
  forwardRef,
  Fragment,
  KeyboardEvent,
  Ref,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Nullable } from '@old/typed/extra/utils';

import ExcelJS from 'exceljs';
import dayjs from 'dayjs';
import UTC from 'dayjs/plugin/utc';

import ErrorCellIcon from '@old/assets/svg/domain/spreadsheet/error-cell-icon.svg';
import WarningCellIcon from '@old/assets/svg/domain/spreadsheet/warning-cell-icon.svg';

import './spreadsheet.css';
import i18n from '../../libs/language';

dayjs.extend(UTC);

type ColResize = ({
  clientX?: number;
  offset?: number;
} | null)[];
type RowResize = ({
  clientY?: number;
  offset?: number;
} | null)[];

type CellType = 'text' | 'option';
type CellValue = string[];
type CellMultiValue = string[];
type CellOptions = string[];
type CellReadonly = boolean;
type CellValidation = ValidationFn;
type CellFeedback = ValidationFeedback;

type BaseCellProps = {
  key: ReturnType<typeof crypto.randomUUID>;
  readonly: CellReadonly;
  validation: CellValidation;
  feedback: CellFeedback;
};
type TextCellProps = { type: Extract<CellType, 'text'>; value: CellValue; options: null };
type ChoiceCellProps = { type: Extract<CellType, 'option'>; value: CellMultiValue; options: CellOptions };
type TextCell = BaseCellProps & TextCellProps;
type ChoiceCell = BaseCellProps & ChoiceCellProps;
type Cell = TextCell | ChoiceCell;

type RowIDX = number;
type ColIDX = number;
type DefinedCellIDX = { row: RowIDX; col: ColIDX };
type EmptyCellIDX = { row: null; col: null };
// type CellIDX = DefinedCellIDX | EmptyCellIDX;

// type RowCoord = number;
// type ColCoord = number;
// type CellCoord = { row: RowCoord; col: ColCoord };
// type EmptyCellCoord = { row: null; col: null };

type ColLabel = string;
type CellLabel = string;
// type CellRangeLabel = string;

export type Spreadsheet = Cell[][];
type SpreadsheetValues = (CellValue | CellMultiValue)[][];
type SpreadsheetHistory = {
  history: Spreadsheet[];
  currentIDX: number;
};

type SelectionRangeCorner = DefinedCellIDX;
type AvailableSelectionRange = { start: SelectionRangeCorner; end: SelectionRangeCorner };
type EmptySelectionRange = { start: EmptyCellIDX; end: EmptyCellIDX };
type SelectionRange = AvailableSelectionRange | EmptySelectionRange;

type FeedbackType = 'error' | 'warning';
type FeedbackMessage = string;
type ValidationFeedback = { type: FeedbackType; message: FeedbackMessage } | null;
export type ValidationFn = (value: CellValue | CellMultiValue) => ValidationFeedback;

type OptionTracker = Map<string, CellOptions | null | undefined>;
type ReadonlyTracker = Map<string, CellReadonly | null | undefined>;
type ValidationTracker = Map<string, ValidationFn | null | undefined>;

type OptionRecordProp = Record<string, CellMultiValue | null | undefined>;
type ReadonlyRecordProp = Record<string, boolean | null | undefined>;
type ValidationRecordProp = Record<string, ValidationFn | null | undefined>;
type SpreadsheetProps = Nullable<{
  data?: (CellValue | CellMultiValue)[][];
  workbook?: ExcelJS.Workbook;
  isSiloRealWorkbook?: boolean;
  initialState?: {
    row: RowIDX;
    col: ColIDX;
  };
  header?: { headerName: string }[];
  fixedRows?: boolean;
  fixedCols?: boolean;
  options?: OptionRecordProp;
  readonly?: ReadonlyRecordProp;
  validation?: ValidationRecordProp;
}>;

type ExternalInterfaceAPI = {
  structure: {
    row: {
      add: (qty: number, index: number) => void;
      remove: (qty: number, index: number) => void;
    };
  };
  resize: {
    column: {
      start: (col: ColIDX, clientX: number) => void;
      end: (col: ColIDX, clientX: number) => void;
      restore: (col: ColIDX) => void;
    };
    row: {
      start: (row: RowIDX, clientY: number) => void;
      end: (row: RowIDX, clientY: number) => void;
      restore: (row: RowIDX) => void;
    };
  };
  content: {
    write: (cellIDX: DefinedCellIDX, text: CellValue[0] | CellMultiValue) => void;
    edit: {
      start: (cellIDX: DefinedCellIDX, type: 'soft' | 'hard', key?: string) => void;
      typing: (text: string) => void;
      confirm: () => void;
      cancel: () => void;
    };
    copy: () => void;
    paste: () => void;
  };
  selection: {
    cell: (cell: DefinedCellIDX) => void;
    edgeCell: (direction: 'up' | 'down' | 'left' | 'right') => void;
    extendToEdgeCell: (direction: 'up' | 'down' | 'left' | 'right') => void;
    row: (row: RowIDX) => void;
    column: (col: ColIDX) => void;
    move: (direction: 'up' | 'down' | 'left' | 'right') => void;
    increase: (direction: 'upwards' | 'downwards' | 'leftwards' | 'rightwards') => void;
    emptyCells: () => void;
    clear: () => void;
  };
  history: {
    undo: () => void;
    redo: () => void;
  };
  feedback: {
    error: {
      register: (message: string, cell: DefinedCellIDX) => void;
    };
    warning: {
      register: (message: string, cell: DefinedCellIDX) => void;
    };
    dismiss: (cell: DefinedCellIDX) => void;
  };
  global: {
    reset: () => void;
  };
};

export type SpreadsheetAPI = {
  action: ExternalInterfaceAPI;
  state: {
    content: Spreadsheet;
    range: {
      area: SelectionRange;
      anchor: SelectionRangeCorner | EmptyCellIDX;
    };
    contentHistory: SpreadsheetHistory;
    columnResize: ColResize;
    rowResize: RowResize;
    workbook?: ExcelJS.Workbook | null;
  };
  mode: {
    edit: boolean;
    selecting: boolean;
    autofilling: boolean;
  };
};

const CONVERSION = {
  column: {
    label: {
      fromColIDX: (n: ColIDX) => {
        const ALPHABET_INIT_ASCII_CODE = 65;
        const ALPHABET_LENGTH = 26;

        let result = '';
        // Convertimos n a base 1 para adaptarnos al sistema tipo Excel (A=1, B=2, ...)
        n = n + 1;
        while (n > 0) {
          const rem = (n - 1) % ALPHABET_LENGTH;
          result = String.fromCharCode(ALPHABET_INIT_ASCII_CODE + rem) + result;
          n = Math.floor((n - 1) / ALPHABET_LENGTH);
        }
        return result;
      },
      fromCellIDX: (cellIDX: DefinedCellIDX): [ColLabel, CellLabel] => {
        const colLabel = CONVERSION.column.label.fromColIDX(cellIDX.col);
        return [colLabel, `${colLabel}${cellIDX.row + 1}`];
      },
      toNumber: (lb: ColLabel) => {
        const ALPHABET_INIT_ASCII_CODE = 65;
        const ALPHABET_LENGTH = 26;

        let result = 0;
        for (let i = 0; i < lb.length; i++) {
          result = result * ALPHABET_LENGTH + (lb.charCodeAt(i) - (ALPHABET_INIT_ASCII_CODE - 1)); // 'A' es 65, por eso se resta 64
        }
        return result - 1; // Convertimos de base 1 a base 0
      },
    },
  },
};

const EXTRACT = {
  tracked: {
    options: {
      forCellIDX: (cellIDX: DefinedCellIDX, options: OptionTracker) => {
        const [colLabel, cellLabel] = CONVERSION.column.label.fromCellIDX(cellIDX);
        return options.get(cellLabel) ?? options.get(colLabel) ?? null;
      },
    },
    readonly: {
      forCellIDX: (cellIDX: DefinedCellIDX, readonly: ReadonlyTracker) => {
        const [colLabel, cellLabel] = CONVERSION.column.label.fromCellIDX(cellIDX);
        return readonly.get(cellLabel) ?? readonly.get(colLabel) ?? false;
      },
    },
    validation: {
      forCellIDX: (cellIDX: DefinedCellIDX, validation: ValidationTracker) => {
        // console.log('[FROM] (CELL IDX) validation:', cellIDX, validation);
        const [colLabel, cellLabel] = CONVERSION.column.label.fromCellIDX(cellIDX);
        // console.log('[FROM] (CELL IDX) labels:', colLabel, cellLabel);
        return validation.get(cellLabel) ?? validation.get(colLabel) ?? (() => null);
      },
    },
  },
};

const CHECK = {
  cell: {
    type: {
      isText: (cell: Cell): cell is TextCell => cell.type === 'text',
      isChoice: (cell: Cell): cell is ChoiceCell => cell.type === 'option',
    },
    index: {
      isEmpty: (cell: DefinedCellIDX | EmptyCellIDX): cell is EmptyCellIDX =>
        cell.row == null || cell.col == null,
    },
  },
};

export const Spreadsheet = forwardRef<SpreadsheetAPI, SpreadsheetProps>(SpreadsheetComponent);

function SpreadsheetComponent(props: SpreadsheetProps, ref: Ref<SpreadsheetAPI>) {
  const DEFAULT_INIT_ROW = 14;
  const DEFAULT_COL_ROW = 5;

  const INIT_ROW = props?.initialState?.row ?? DEFAULT_INIT_ROW;
  const INIT_COL = props?.initialState?.col ?? DEFAULT_COL_ROW;

  const { data, workbook, isSiloRealWorkbook } = props;
  const { header } = props;
  const fixedRows = props?.fixedRows ?? false;
  const fixedCols = props?.fixedCols ?? false;

  const options = new Map(Object.entries(props?.options ?? {}));
  const readonly = new Map(Object.entries(props?.readonly ?? {}));
  const validation = new Map(Object.entries(props?.validation ?? {}));

  const initColumnResize = useMemo(() => range(INIT_COL, () => null), []);
  const initRowResize = useMemo(() => range(INIT_ROW, () => null), []);

  // [RESIZE]
  const [columnResize, setColumnResize] = useState<ColResize>(initColumnResize);
  const [rowResize, setRowResize] = useState<RowResize>(initRowResize);

  // [CONTENT]
  const DEFAULT_TEXT_CELL: Omit<TextCell, 'key'> = {
    type: 'text',
    value: [''],
    options: null,
    readonly: false,
    validation: () => null,
    feedback: null,
  };

  const [contentTrackers, setContentTrackers] = useState({ options, readonly, validation });

  const initContent = useMemo(
    () =>
      range(INIT_ROW, (i) =>
        range(INIT_COL, (j) =>
          initializeCell({ row: i, col: j }, { value: data?.[i]?.[j], options, readonly, validation })
        )
      ),
    []
  );
  const [content, setContent] = useState<Spreadsheet>(initContent);
  const [temporaryCellContent, setTemporaryCellContent] = useState<string | null>(null);

  // [HISTORY]
  const initContentHistory = { history: [initContent], currentIDX: 0 };
  const [contentHistory, setContentHistory] = useState<SpreadsheetHistory>(initContentHistory);

  // [SELECTION]
  const initRangeAnchorCell = { row: null, col: null };
  const initCellRange = { start: initRangeAnchorCell, end: initRangeAnchorCell };
  const [targetCellRange, setTargetCellRange] = useState<SelectionRange>(initCellRange);
  const [targetRangeAnchorCell, setTargetRangeAnchorCell] = useState<SelectionRangeCorner | EmptyCellIDX>(
    initRangeAnchorCell
  );
  const isSelectionRangePresent = targetCellRange.start.row != null && targetCellRange.start.col != null;

  // [FEEDBACK]
  const hasErrorOnAnchorCell =
    content[targetRangeAnchorCell.row ?? -1]?.[targetRangeAnchorCell.col ?? -1].feedback?.type === 'error';
  const hasWarningOnAnchorCell =
    content[targetRangeAnchorCell.row ?? -1]?.[targetRangeAnchorCell.col ?? -1].feedback?.type === 'warning';

  // [MODES]
  const [editMode, setEditMode] = useState(false);
  const [selectingMode, setSelectingMode] = useState(false);
  const [autofillingMode, setAutofillingMode] = useState(false);
  // TODO: ¿eliminar?
  // const [isHardEditMode, setIsHardEditMode] = useState(false);
  // const [resizingMode, setResizingMode] = useState(false);

  // [REF]
  const dragEnterCallingTwice = useRef<SelectionRangeCorner | EmptyCellIDX>({ row: null, col: null });
  const spreadsheetBodyRef = useRef<HTMLDivElement | null>(null);
  const contentBarInputRef = useRef<HTMLInputElement | null>(null);

  // [LOGIC]
  useEffectOnUpdate(() => {
    if (!spreadsheetBodyRef.current) return;
    if (!editMode) spreadsheetBodyRef.current.focus();
  }, [editMode]);

  const lastRow = content.length - 1;
  const lastCol = content[0].length - 1;

  // [HANDLERS]
  const INTERNAL_HANDLER = {
    structure: {
      // TODO: pensar si es buena idea implementar el agregado de filas/columnas pasando explícitamente la(s) fila(s)/columna(s) que se quieren agregar y el índice donde se quiere agregar, porque al pasar el parámetro QTY: number, obliga a que cuando se quiere agregar una columna que ya tenga que tener contenido desde el principio (como en el copiado y pegado)
      row: {
        add: (qty: number, index: number) => {
          if (editMode || fixedRows) return;

          // Cálculo para aceptar índices negativos
          const relativeIndex = index >= 0 ? index : Math.max(content.length + index + 1, 0);

          // Agregado de filas
          setContent((prev) => {
            const headSlice = prev.slice(0, relativeIndex);
            const tailSlice = prev.slice(relativeIndex);

            // PROBAR: que estén quedando con el trackeo correcto las nuevas filas (readonly, validaciones, etc) ❌ (no está funcionando con agregados intermedios, solo con los agregados al final)
            const newRows = range(qty, (i) =>
              range(prev[0].length, (j) =>
                initializeCell({ row: relativeIndex + i, col: j }, contentTrackers)
              )
            );
            return [...headSlice, ...newRows, ...tailSlice];
          });

          // Agregado de resizes de filas
          setRowResize((prev) => [
            ...prev.slice(0, relativeIndex),
            ...range(qty, () => null),
            ...prev.slice(relativeIndex),
          ]);
        },
        remove: (qty: number, index: number) => {
          if (editMode || fixedRows) return;

          // Cálculo para aceptar índices negativos
          const relativeIndex = index >= 0 ? index : Math.max(content.length + index, 0);

          // Borrado de filas
          setContent((prev) => [...prev.slice(0, relativeIndex), ...prev.slice(relativeIndex + qty)]);

          // Borrado de resizes de filas
          setRowResize((prev) => [...prev.slice(0, relativeIndex), ...prev.slice(relativeIndex + qty)]);
        },
        resize: {
          captureInitPos: (row: RowIDX, currentPos: number) => {
            // Se captura la posición inicial del mouse
            setRowResize((prev) => {
              const prevConfig = prev[row] ?? {};

              const newRowResizeConfig = { ...prevConfig, clientY: currentPos };
              const newRowResize = [...prev];
              newRowResize[row] = newRowResizeConfig;
              return newRowResize;
            });
          },
          computeOffset: (row: RowIDX, currentPos: number) => {
            // Se calcula el diferencial de posicionamiento respecto de la captura de la posición inicial
            setRowResize((prev) => {
              const prevConfig = prev[row] ?? {};
              const prevClientY = prev[row]?.clientY ?? 0;

              const offsetDiff = currentPos - prevClientY;
              const currentOffset = prevConfig.offset ?? 20;
              const relativeOffset = currentOffset + offsetDiff < 20 ? 0 : currentOffset + offsetDiff;
              const offset = Math.max(relativeOffset, 0);

              const newRowResizeConfig = { ...prevConfig, offset };
              const newRowResize = [...prev];
              newRowResize[row] = newRowResizeConfig;
              return newRowResize;
            });
          },
          reset: (row: RowIDX) => {
            // Se restaura el offset a un valor por defecto
            setRowResize((prev) => {
              const prevConfig = prev[row] ?? {};

              const newRowResizeConfig = { ...prevConfig, offset: 20 };
              const newRowResize = [...prev];
              newRowResize[row] = newRowResizeConfig;
              return newRowResize;
            });
          },
        },
      },
      col: {
        add: (qty: number, index: number) => {
          if (editMode || fixedCols) return;

          // Cálculo para aceptar índices negativos
          const relativeIndex = index >= 0 ? index : Math.max(content[0].length + index + 1, 0);

          // Agregado de columnas
          setContent((prev) =>
            prev.map((row) => [
              ...row.slice(0, relativeIndex),
              ...range(qty, () => ({
                key: crypto.randomUUID(),
                ...DEFAULT_TEXT_CELL,
              })),
              ...row.slice(relativeIndex),
            ])
          );

          // PROBAR: el agregado de columnas y que las validaciones se mueven (y las opciones, etc) ❌ (no está funcionando con agregados intermedios, solo con los agregados al final)
          setContentTrackers((prev) => {
            const newOptions = new Map(prev.options);
            const newReadonly = new Map(prev.readonly);
            const newValidation = new Map(prev.validation);

            range(qty).forEach((_, i) => {
              const newColLabel = CONVERSION.column.label.fromColIDX(relativeIndex + i);
              const regex = new RegExp(`^${newColLabel}(\\d*)$`);

              for (const [key, value] of newOptions) {
                const match = key.match(regex);
                if (match) newOptions.set(newColLabel + match[1], value);
              }
              for (const [key, value] of newReadonly) {
                const match = key.match(regex);
                if (match) newReadonly.set(newColLabel + match[1], value);
              }
              for (const [key, value] of newValidation) {
                const match = key.match(regex);
                if (match) newValidation.set(newColLabel + match[1], value);
              }
            });

            return {
              options: newOptions,
              readonly: newReadonly,
              validation: newValidation,
            };
          });

          // Agregado de resizes de columnas
          setColumnResize((prev) => [
            ...prev.slice(0, relativeIndex),
            ...range(qty, () => null),
            ...prev.slice(relativeIndex),
          ]);
        },
        remove: (qty: number, index: number) => {
          if (editMode || fixedCols) return;

          // Cálculo para aceptar índices negativos
          const relativeIndex = index >= 0 ? index : Math.max(content[0].length + index, 0);

          // Borrado de columnas
          setContent((prev) =>
            prev.map((row) => [...row.slice(0, relativeIndex), ...row.slice(relativeIndex + qty)])
          );

          // Borrado de resizes de columnas
          setColumnResize((prev) => [...prev.slice(0, relativeIndex), ...prev.slice(relativeIndex + qty)]);
        },
        resize: {
          captureInitPos: (col: ColIDX, currentPos: number) => {
            // Se captura la posición inicial del mouse
            setColumnResize((prev) => {
              const prevConfig = prev[col] ?? {};

              const newColumnResizeConfig = { ...prevConfig, clientX: currentPos };
              const newColumnResize = [...prev];
              newColumnResize[col] = newColumnResizeConfig;
              return newColumnResize;
            });
          },
          computeOffset: (col: ColIDX, currentPos: number) => {
            // Se calcula el diferencial de posicionamiento respecto de la captura de la posición inicial
            setColumnResize((prev) => {
              const prevConfig = prev[col] ?? {};
              const prevClientX = prev[col]?.clientX ?? 0;

              const offsetDiff = currentPos - prevClientX;
              const currentOffset = prevConfig.offset ?? 128;
              const offset = Math.max(currentOffset + offsetDiff, 0);

              const newColumnResizeConfig = { ...prevConfig, offset };
              const newColumnResize = [...prev];
              newColumnResize[col] = newColumnResizeConfig;
              return newColumnResize;
            });
          },
          reset: (col: ColIDX) => {
            // Se restaura el offset a un valor por defecto
            setColumnResize((prev) => {
              const prevConfig = prev[col] ?? {};

              const newColumnResizeConfig = { ...prevConfig, offset: 128 };
              const newColumnResize = [...prev];
              newColumnResize[col] = newColumnResizeConfig;
              return newColumnResize;
            });
          },
        },
      },
    },
    content: {
      write: (text: string, { row, col }: DefinedCellIDX) =>
        INTERNAL_HANDLER.content.writeBulk([[[text]]], { row, col }),
      multiWrite: (texts: CellMultiValue, { row, col }: DefinedCellIDX) =>
        INTERNAL_HANDLER.content.writeBulk([[texts]], { row, col }),
      writeBulk: (texts: SpreadsheetValues, initCell: DefinedCellIDX) => {
        setContent((prevContent) => {
          let newContent = prevContent;

          // config?: { type: 'soft' | 'hard' }
          // console.log(config);

          const textRowsQty = texts.length;
          const textColsQty = texts[0].length;

          const isRowOverflow = initCell.row + textRowsQty > newContent.length;
          const isColOverflow = initCell.col + textColsQty > newContent[0].length;

          if (isRowOverflow && !fixedRows) {
            const rowsInOverflow = initCell.row + textRowsQty - newContent.length;

            const newRows = range(rowsInOverflow, (i) =>
              range(newContent[0].length, (j) =>
                initializeCell({ row: lastRow + i + 1, col: j }, contentTrackers)
              )
            );

            // El agregado de filas automático no puede hacerse con INTERNAL_HANDLER.structure.row.add
            // porque los estados en React se actualizan de forma asincrónica (no llega a agregarse antes de escribir)
            // Así que hay que hacerlo a mano:
            newContent = newContent.concat(newRows);
            setRowResize((prev) => prev.concat(range(rowsInOverflow, () => null)));
          }

          if (isColOverflow && !fixedCols) {
            const colsInOverflow = initCell.col + textColsQty - newContent[0].length;

            const newCols = range(colsInOverflow, (j) =>
              initializeCell({ row: 0, col: lastCol + j + 1 }, contentTrackers)
            );

            // El agregado de columnas automático no puede hacerse con INTERNAL_HANDLER.structure.col.add
            // porque los estados en React se actualizan de forma asincrónica (no llega a agregarse antes de escribir)
            // Así que hay que hacerlo a mano:
            newContent = newContent.map((row) => row.concat(newCols));
            setColumnResize((prev) => prev.concat(range(colsInOverflow, () => null)));
          }

          // console.log('matrix writing');
          texts.forEach((textRow, idx) => {
            const rowIDX = initCell.row + idx;
            if (rowIDX > newContent.length - 1) {
              // console.log('fila descartada', idx);
              return;
            }

            // console.groupCollapsed('fila', idx);

            textRow.forEach((newArrayText, idx) => {
              const colIDX = initCell.col + idx;
              if (colIDX > newContent[0].length - 1) {
                // console.log('columna descartada', idx);
                return;
              }

              // console.log('columna', idx);

              const cell = { ...newContent[rowIDX][colIDX] };
              // console.log(cell);

              if (cell.readonly) return;

              const isChoiceCell = CHECK.cell.type.isChoice(cell);
              const isTextCell = CHECK.cell.type.isText(cell);

              const isEmptyValue = newArrayText.length === 1 && newArrayText[0] === '';

              if (isChoiceCell && !isEmptyValue) {
                const choices: CellMultiValue = newArrayText;

                const isValidChoice = choices
                  .map((c) => c.toLowerCase())
                  .some((text) =>
                    text !== '' ? cell.options.map((o) => o.toLowerCase()).includes(text) : true
                  );

                const invalidChoiceFeedback: ValidationFeedback = {
                  type: 'warning',
                  message: i18n.t('app.massive_register.not_available_option_error'),
                };
                cell.feedback = cell.validation(choices) ?? isValidChoice ? null : invalidChoiceFeedback;
              }

              if (isTextCell && !isEmptyValue) cell.feedback = cell.validation(newArrayText);

              if (isEmptyValue) cell.feedback = null;

              const newCell = { ...cell, value: newArrayText } as Cell;
              newContent = newContent.with(rowIDX, newContent[rowIDX].with(colIDX, newCell));
            });
            // console.groupEnd();
          });

          // console.log('final matrix', newContent);

          INTERNAL_HANDLER.history.newEntry(newContent);
          return newContent;
        });
      },
      draft: {
        write: (text: string) => setTemporaryCellContent(text),
        commit: ({ row, col }: DefinedCellIDX) => {
          const cell = content[row][col];
          if (!cell) return;

          const newValue = [...cell.value];
          newValue[newValue.length - 1] = temporaryCellContent ?? '';
          INTERNAL_HANDLER.content.multiWrite(
            newValue.slice(0, 1).concat(newValue.slice(1).filter((s) => s !== '')),
            { row, col }
          );
          setTemporaryCellContent(null);
        },
        discard: () => setTemporaryCellContent(null),
      },
    },
    selection: {
      cell: (selectedCell: DefinedCellIDX) => {
        setTargetRangeAnchorCell(selectedCell);
        setTargetCellRange({ start: selectedCell, end: selectedCell });

        const { row, col } = selectedCell;
        if (row === lastRow && !fixedRows) INTERNAL_HANDLER.structure.row.add(1, -1);
        if (col === lastCol && !fixedCols) INTERNAL_HANDLER.structure.col.add(1, -1);
      },
      area: (anchorCell: DefinedCellIDX, newBoundaryCell: DefinedCellIDX) => {
        setTargetRangeAnchorCell(anchorCell);
        setTargetCellRange(calculateNewRangeState({ anchorCell, newRange: newBoundaryCell }));
      },
      move: {
        up: (anchorCell: SelectionRangeCorner | EmptyCellIDX) => {
          if (isEmptyCell(anchorCell)) {
            INTERNAL_HANDLER.selection.cell({ row: 0, col: 0 });
            return;
          }
          const { row, col } = anchorCell;
          const newRow = Math.max(row - 1, 0);
          INTERNAL_HANDLER.selection.cell({ row: newRow, col });
        },
        down: (anchorCell: SelectionRangeCorner | EmptyCellIDX) => {
          if (isEmptyCell(anchorCell)) {
            INTERNAL_HANDLER.selection.cell({ row: 0, col: 0 });
            return;
          }
          const { row, col } = anchorCell;
          const newRow = Math.min(row + 1, content.length - 1);
          INTERNAL_HANDLER.selection.cell({ row: newRow, col });
        },
        left: (anchorCell: SelectionRangeCorner | EmptyCellIDX) => {
          if (isEmptyCell(anchorCell)) {
            INTERNAL_HANDLER.selection.cell({ row: 0, col: 0 });
            return;
          }
          const { row, col } = anchorCell;
          const newCol = Math.max(col - 1, 0);
          INTERNAL_HANDLER.selection.cell({ row, col: newCol });
        },
        right: (anchorCell: SelectionRangeCorner | EmptyCellIDX) => {
          if (isEmptyCell(anchorCell)) {
            INTERNAL_HANDLER.selection.cell({ row: 0, col: 0 });
            return;
          }
          const { row, col } = anchorCell;
          const newCol = Math.min(col + 1, content[0].length - 1);
          INTERNAL_HANDLER.selection.cell({ row, col: newCol });
        },
      },
      increase: {
        upwards: (anchorCell: SelectionRangeCorner | EmptyCellIDX, cellRange: SelectionRange) => {
          if (isEmptyCell(anchorCell)) return;

          const { row: anchorRow, col: anchorCol } = anchorCell;
          const {
            start: { row: rowStart, col: colStart },
            end: { row: rowEnd, col: colEnd },
          } = cellRange;

          const newRow = rowEnd !== anchorRow ? rowEnd! - 1 : rowStart! - 1;
          const newCol = colStart !== anchorCol ? colStart! : colEnd!;
          const newRange = { row: Math.max(newRow, 0), col: newCol };

          INTERNAL_HANDLER.selection.area(anchorCell, newRange);
        },
        downwards: (anchorCell: SelectionRangeCorner | EmptyCellIDX, cellRange: SelectionRange) => {
          if (isEmptyCell(anchorCell)) return;

          const { row: anchorRow, col: anchorCol } = anchorCell;
          const {
            start: { row: rowStart, col: colStart },
            end: { row: rowEnd, col: colEnd },
          } = cellRange;

          const newRow = rowStart !== anchorRow ? rowStart! + 1 : rowEnd! + 1;
          const newCol = colStart !== anchorCol ? colStart! : colEnd!;
          const newRange = { row: Math.min(newRow, content.length - 1), col: newCol };

          INTERNAL_HANDLER.selection.area(anchorCell, newRange);
        },
        leftwards: (anchorCell: SelectionRangeCorner | EmptyCellIDX, cellRange: SelectionRange) => {
          if (isEmptyCell(anchorCell)) return;

          const { row: anchorRow, col: anchorCol } = anchorCell;
          const {
            start: { row: rowStart, col: colStart },
            end: { row: rowEnd, col: colEnd },
          } = cellRange;

          const newCol = colEnd !== anchorCol ? colEnd! - 1 : colStart! - 1;
          const newRow = rowStart !== anchorRow ? rowStart! : rowEnd!;
          const newRange = { row: newRow, col: Math.max(newCol, 0) };

          INTERNAL_HANDLER.selection.area(anchorCell, newRange);
        },
        rightwards: (anchorCell: SelectionRangeCorner | EmptyCellIDX, cellRange: SelectionRange) => {
          if (isEmptyCell(anchorCell)) return;

          const { row: anchorRow, col: anchorCol } = anchorCell;
          const {
            start: { row: rowStart, col: colStart },
            end: { row: rowEnd, col: colEnd },
          } = cellRange;

          const newCol = colStart !== anchorCol ? colStart! + 1 : colEnd! + 1;
          const newRow = rowStart !== anchorRow ? rowStart! : rowEnd!;
          const newRange = { row: newRow, col: Math.min(newCol, content[0].length - 1) };

          INTERNAL_HANDLER.selection.area(anchorCell, newRange);
        },
      },
      directional: {
        area: (anchorCell: DefinedCellIDX, newBoundaryCell: DefinedCellIDX) => {
          setTargetRangeAnchorCell(anchorCell);
          setTargetCellRange(calculateNewShrinkedRangeState({ anchorCell, newRange: newBoundaryCell }));
        },
        commit: (anchorCell: DefinedCellIDX, cellRange: SelectionRange) => {
          if (!isAvailableSelectionRange(cellRange)) return;

          const { start: cellStart } = cellRange;
          const { row: rowStart, col: colStart } = cellStart;
          const isAnchorTheStartRangeCell = anchorCell.row === rowStart && anchorCell.col === colStart;

          // Determinar si la selección de autocompletado es en columna o en fila
          const isColumnDirectionalSelection = cellRange.start.row !== cellRange.end.row;

          // La cantidad de celdas que abarca la selección de autocompletado independientemente de la dirección
          const selectionLength =
            cellRange.end.row - cellRange.start.row + (cellRange.end.col - cellRange.start.col) + 1;

          const cell = content[anchorCell.row][anchorCell.col];

          // TODO: falta hacer la lógica que verifique que si tiene 2 casillas continuas con valores numéricos, que calcule la diferencia entre ambos y lo aplique al autocompletado
          const AUTOCOMPLETE_BY_TYPE = {
            text: (i: number) => {
              const [text] = cell.value;
              const isTextNumber = !isNaN(Number(text));
              return [isTextNumber ? (Number(text) + i).toString() : text];
            },
            option: () => cell.value,
          };

          const rawTextArray = range(selectionLength, AUTOCOMPLETE_BY_TYPE[cell.type]);

          // Para que el autocompletado sea direccional, debe considerarse invertirlo
          // en caso de que el arrastre sea hacia arriba o hacia la izquierda
          if (!isAnchorTheStartRangeCell) rawTextArray.reverse();

          // Si el autocompletado es en columna, cada texto debe estar en un array: [['1'], ['2'], ['3']]
          // Si es en fila, los textos deben envolverse en 2 arrays: [['1', '2', '3']]
          const textArray = isColumnDirectionalSelection ? rawTextArray.map((t) => [t]) : [rawTextArray];

          INTERNAL_HANDLER.content.writeBulk(textArray, cellStart);
        },
      },
      emptyCellContent: (cellRange: SelectionRange) => {
        if (!isAvailableSelectionRange(cellRange)) return;

        const { start, end } = cellRange;
        const relativeRow = Math.abs(start.row - end.row);
        const relativeCol = Math.abs(start.col - end.col);

        const emptyCells = range(relativeRow + 1, () => range(relativeCol + 1, () => ['']));

        INTERNAL_HANDLER.content.writeBulk(emptyCells, start);
      },
      clear: () => {
        setTargetRangeAnchorCell(initRangeAnchorCell);
        setTargetCellRange(initCellRange);
      },
    },
    history: {
      newEntry: (newContent: Spreadsheet) => {
        const MAX_HISTORY_LENGTH = 15;
        setContentHistory(({ history, currentIDX }) => ({
          history: history
            .slice(0, currentIDX + 1)
            .concat([newContent])
            .slice(-MAX_HISTORY_LENGTH),
          currentIDX: Math.min(currentIDX + 1, MAX_HISTORY_LENGTH - 1),
        }));
      },
      changeIndex: (newIDX: number) => {
        const isTooLow = newIDX < 0;
        const isTooHigh = newIDX > contentHistory.history.length - 1;
        if (isTooLow || isTooHigh) return;

        const { history } = contentHistory;

        setContent(history[newIDX]);
        setContentHistory({ history, currentIDX: newIDX });
      },
    },
    feedback: {
      error: {
        register: (message: FeedbackMessage, { row, col }: DefinedCellIDX) => {
          setContent((prev) => {
            const cell = prev[row]?.[col];
            if (!cell) return prev;
            const newCell = { ...cell, feedback: { type: 'error', message } } as Cell;
            const newRow = prev[row].with(col, newCell);
            return prev.with(row, newRow);
          });
        },
      },
      warning: {
        register: (message: FeedbackMessage, { row, col }: DefinedCellIDX) => {
          setContent((prev) => {
            const cell = prev[row]?.[col];
            if (!cell) return prev;
            const newCell = { ...cell, feedback: { type: 'warning', message } } as Cell;
            const newRow = prev[row].with(col, newCell);
            return prev.with(row, newRow);
          });
        },
      },
      dismiss: ({ row, col }: DefinedCellIDX) => {
        const updateFeedback = (prev: Spreadsheet) => {
          const cell = prev[row][col];
          const newCell = { ...cell, feedback: null } as Cell;
          const newRow = prev[row].with(col, newCell);
          return prev.with(row, newRow);
        };

        const hasErrorPresent = content?.[row]?.[col]?.feedback?.type === 'error';
        const hasWarningPresent = content?.[row]?.[col]?.feedback?.type === 'warning';

        if (hasErrorPresent) setContent(updateFeedback);
        if (hasWarningPresent) setContent(updateFeedback);
      },
    },
    mode: {
      edit: {
        on: () => setEditMode(true),
        off: () => setEditMode(false),
      },
      selecting: {
        on: () => setSelectingMode(true),
        off: () => setSelectingMode(false),
      },
      // resizing: {
      //   on: () => setResizingMode(true),
      //   off: () => setResizingMode(false),
      // },
      autocompleting: {
        on: () => setAutofillingMode(true),
        off: () => setAutofillingMode(false),
      },
    },
    global: {
      reset: () => {
        setColumnResize(initColumnResize);
        setRowResize(initRowResize);
        setContent(initContent);
        setTemporaryCellContent(null);
        setContentTrackers({ options, readonly, validation });
        setContentHistory(initContentHistory);
        setTargetCellRange(initCellRange);
        setTargetRangeAnchorCell(initRangeAnchorCell);
        setEditMode(false);
        setSelectingMode(false);
        setAutofillingMode(false);
        dragEnterCallingTwice.current = { row: null, col: null };
        spreadsheetBodyRef.current = null;
        contentBarInputRef.current = null;
        setOfferColumnMapping(false);
        setActiveColumnMappingUI(false);
        setColumnMapping(new Map());
      },
    },
  };

  const HANDLER = {
    action: {
      structure: {
        row: {
          add: (qty: number, index: number) => INTERNAL_HANDLER.structure.row.add(qty, index),
          remove: (qty: number, index: number) => INTERNAL_HANDLER.structure.row.remove(qty, index),
        },
      },
      resize: {
        column: {
          start: (col: ColIDX, clientX: number) =>
            INTERNAL_HANDLER.structure.col.resize.captureInitPos(col, clientX),
          end: (col: ColIDX, clientX: number) =>
            INTERNAL_HANDLER.structure.col.resize.computeOffset(col, clientX),
          restore: (col: ColIDX) => INTERNAL_HANDLER.structure.col.resize.reset(col),
        },
        row: {
          start: (row: RowIDX, clientY: number) =>
            INTERNAL_HANDLER.structure.row.resize.captureInitPos(row, clientY),
          end: (row: RowIDX, clientY: number) =>
            INTERNAL_HANDLER.structure.row.resize.computeOffset(row, clientY),
          restore: (row: RowIDX) => INTERNAL_HANDLER.structure.row.resize.reset(row),
        },
      },
      content: {
        write: (cellIDX: DefinedCellIDX, text: CellValue[0] | CellMultiValue) => {
          const HANDLER_BY_TYPE = {
            text: () => INTERNAL_HANDLER.content.write(text as CellValue[0], cellIDX),
            option: () => INTERNAL_HANDLER.content.multiWrite(text as CellMultiValue, cellIDX),
          };
          HANDLER_BY_TYPE[content[cellIDX.row][cellIDX.col].type]();
        },
        edit: {
          start: (cellIDX: DefinedCellIDX, type: 'soft' | 'hard', key?: string) => {
            const cell = content[cellIDX.row][cellIDX.col];
            if (cell.readonly) {
              INTERNAL_HANDLER.selection.cell(cellIDX);
              return;
            }
            if (!editMode) {
              INTERNAL_HANDLER.selection.cell(cellIDX);

              const newDraft =
                type === 'hard'
                  ? key !== 'Backspace'
                    ? key ?? ''
                    : ''
                  : content[cellIDX.row][cellIDX.col].value.at(-1);
              INTERNAL_HANDLER.content.draft.write(newDraft!);
              INTERNAL_HANDLER.mode.edit.on();
              return;
            }

            const isSameCell =
              targetRangeAnchorCell.row === cellIDX.row && targetRangeAnchorCell.col === cellIDX.col;
            if (isSameCell) return;

            INTERNAL_HANDLER.content.draft.commit(targetRangeAnchorCell as DefinedCellIDX);
            INTERNAL_HANDLER.selection.cell(cellIDX);
          },
          typing: (text: string) => {
            if (!editMode) INTERNAL_HANDLER.mode.edit.on(); // OLD IMPLEMENTATION: if (!editMode) INTERNAL_HANDLER.mode.edit.on('hard');
            INTERNAL_HANDLER.content.draft.write(text);
          },
          confirm: () => {
            INTERNAL_HANDLER.content.draft.commit(targetRangeAnchorCell as DefinedCellIDX);
            INTERNAL_HANDLER.mode.edit.off();
          },
          cancel: () => {
            INTERNAL_HANDLER.content.draft.discard();
            INTERNAL_HANDLER.mode.edit.off();
          },
        },
        copy: () => {
          const subMatrixContent = content
            .slice(targetCellRange.start.row!, targetCellRange.end.row! + 1)
            .map((row) => row.slice(targetCellRange.start.col!, targetCellRange.end.col! + 1));

          const text = subMatrixContent
            .map((row) => row.map((cell) => cell.value.toString()).join('\t'))
            .join('\n');

          navigator.clipboard.writeText(text);
        },
        paste: () => {
          navigator.clipboard.readText().then((text) => {
            const inputs = text
              .trim()
              .split('\n')
              .map((s) => s.split('\t').map((s) => [s]));

            INTERNAL_HANDLER.content.writeBulk(inputs, targetRangeAnchorCell as DefinedCellIDX);
          });
        },
      },
      selection: {
        cell: (cell: DefinedCellIDX) => {
          if (editMode) {
            INTERNAL_HANDLER.content.draft.commit(targetRangeAnchorCell as DefinedCellIDX);
            INTERNAL_HANDLER.mode.edit.off();
          }
          INTERNAL_HANDLER.selection.cell(cell);
        },
        edgeCell: (direction: 'up' | 'down' | 'left' | 'right') => {
          if (isEmptyCell(targetRangeAnchorCell)) return;

          const { row, col } = targetRangeAnchorCell;
          const DESTINY_CELL = {
            up: { row: 0, col },
            down: { row: content.length - 1, col },
            left: { row, col: 0 },
            right: { row, col: content[0].length - 1 },
          };
          INTERNAL_HANDLER.selection.cell(DESTINY_CELL[direction]);
        },
        extendToEdgeCell: (direction: 'up' | 'down' | 'left' | 'right') => {
          if (isEmptyCell(targetRangeAnchorCell)) return;

          const { row, col } = targetRangeAnchorCell;
          const DESTINY_CELL = {
            up: { row: 0, col },
            down: { row: content.length - 1, col },
            left: { row, col: 0 },
            right: { row, col: content[0].length - 1 },
          };
          INTERNAL_HANDLER.selection.directional.area(
            targetRangeAnchorCell as DefinedCellIDX,
            DESTINY_CELL[direction]
          );
        },
        row: (row: RowIDX) =>
          INTERNAL_HANDLER.selection.area({ row, col: 0 }, { row, col: content[0].length - 1 }),
        column: (col: ColIDX) =>
          INTERNAL_HANDLER.selection.area({ row: 0, col }, { row: content.length - 1, col }),
        move: (direction: 'up' | 'down' | 'left' | 'right') =>
          INTERNAL_HANDLER.selection.move[direction](targetRangeAnchorCell),
        increase: (direction: 'upwards' | 'downwards' | 'leftwards' | 'rightwards') =>
          INTERNAL_HANDLER.selection.increase[direction](targetRangeAnchorCell, targetCellRange),
        emptyCells: () => INTERNAL_HANDLER.selection.emptyCellContent(targetCellRange),
        clear: () => INTERNAL_HANDLER.selection.clear(),
        drag: {
          start: (cell: DefinedCellIDX) => {
            INTERNAL_HANDLER.selection.cell(cell);
            INTERNAL_HANDLER.mode.selecting.on();
          },
          trackCellTraverse: (cell: DefinedCellIDX) => {
            if (!selectingMode) return;

            const isSameDragPosition =
              dragEnterCallingTwice.current.row === cell.row &&
              dragEnterCallingTwice.current.col === cell.col;
            if (isSameDragPosition) return;

            INTERNAL_HANDLER.selection.area(targetRangeAnchorCell as DefinedCellIDX, cell);
          },
          end: () => INTERNAL_HANDLER.mode.selecting.off(),
        },
        autocomplete: {
          start: (cell: DefinedCellIDX) => {
            INTERNAL_HANDLER.selection.cell(cell);
            INTERNAL_HANDLER.mode.autocompleting.on();
          },
          trackCellTraverse: (cell: DefinedCellIDX) => {
            if (!autofillingMode) return;

            const isSameDragPosition =
              dragEnterCallingTwice.current.row === cell.row &&
              dragEnterCallingTwice.current.col === cell.col;
            if (isSameDragPosition) return;

            INTERNAL_HANDLER.selection.directional.area(targetRangeAnchorCell as DefinedCellIDX, cell);
          },
          end: () => {
            INTERNAL_HANDLER.mode.autocompleting.off();
            INTERNAL_HANDLER.selection.directional.commit(
              targetRangeAnchorCell as DefinedCellIDX,
              targetCellRange
            );
          },
        },
      },
      history: {
        undo: () => INTERNAL_HANDLER.history.changeIndex(contentHistory.currentIDX - 1),
        redo: () => INTERNAL_HANDLER.history.changeIndex(contentHistory.currentIDX + 1),
      },
      feedback: {
        error: {
          register: (message: FeedbackMessage, cell: DefinedCellIDX) =>
            INTERNAL_HANDLER.feedback.error.register(message, cell),
        },
        warning: {
          register: (message: FeedbackMessage, cell: DefinedCellIDX) =>
            INTERNAL_HANDLER.feedback.warning.register(message, cell),
        },
        dismiss: (cell: DefinedCellIDX) => INTERNAL_HANDLER.feedback.dismiss(cell),
      },
      global: {
        reset: INTERNAL_HANDLER.global.reset,
      },
    },
    shortcut: {
      table: {
        escape: (e: KeyboardEvent<HTMLDivElement>) => {
          const isEscapeKey = e.key === 'Escape';
          if (!isEscapeKey) return;

          if (!isEmptyCell(targetRangeAnchorCell)) e.stopPropagation();

          HANDLER.action.selection.clear();
        },
        delete: (e: KeyboardEvent<HTMLDivElement>) => {
          const isDeleteKey = e.key === 'Delete';
          if (!isDeleteKey) return;

          HANDLER.action.selection.emptyCells();
        },
        typing: (e: KeyboardEvent<HTMLDivElement>) => {
          const IS_ENABLING_EDIT_MODE = {
            soft: ['Enter', 'F2'].some((key) => e.key.match(key)), // Soft editing: preserves the content and enable the edit mode
            hard:
              ['Backspace', /^.$/gi].some((key) => e.key.match(key)) &&
              // !e.shiftKey &&
              !e.ctrlKey &&
              !e.altKey &&
              !e.metaKey, // Hard editing: clear the content and enable the edit mode
          };

          if (IS_ENABLING_EDIT_MODE.soft) {
            e.preventDefault();
            HANDLER.action.content.edit.start(targetRangeAnchorCell as DefinedCellIDX, 'soft');
          }
          if (IS_ENABLING_EDIT_MODE.hard) {
            e.preventDefault();
            HANDLER.action.content.edit.start(targetRangeAnchorCell as DefinedCellIDX, 'hard', e.key);
          }
        },
        arrow: {
          up: (e: KeyboardEvent<HTMLDivElement>) => {
            const isArrowUpKey = e.key === 'ArrowUp';
            if (!isArrowUpKey) return;

            e.preventDefault();

            HANDLER.action.selection.move('up');
          },
          down: (e: KeyboardEvent<HTMLDivElement>) => {
            const isArrowDownKey = e.key === 'ArrowDown';
            if (!isArrowDownKey) return;

            e.preventDefault();

            HANDLER.action.selection.move('down');
          },
          left: (e: KeyboardEvent<HTMLDivElement>) => {
            const isArrowLeftKey = e.key === 'ArrowLeft';
            if (!isArrowLeftKey) return;

            e.preventDefault();

            HANDLER.action.selection.move('left');
          },
          right: (e: KeyboardEvent<HTMLDivElement>) => {
            const isArrowRightKey = e.key === 'ArrowRight';
            if (!isArrowRightKey) return;

            e.preventDefault();

            HANDLER.action.selection.move('right');
          },
        },
        home: (e: KeyboardEvent<HTMLDivElement>) => {
          const isHomeKey = e.key === 'Home';
          if (!isHomeKey) return;

          e.preventDefault();

          HANDLER.action.selection.edgeCell('left');
        },
        end: (e: KeyboardEvent<HTMLDivElement>) => {
          const isEndKey = e.key === 'End';
          if (!isEndKey) return;

          e.preventDefault();

          HANDLER.action.selection.edgeCell('right');
        },
        pageUp: (e: KeyboardEvent<HTMLDivElement>) => {
          const isPageUpKey = e.key === 'PageUp';
          if (!isPageUpKey) return;

          e.preventDefault();

          HANDLER.action.selection.edgeCell('up');
        },
        pageDown: (e: KeyboardEvent<HTMLDivElement>) => {
          const isPageDownKey = e.key === 'PageDown';
          if (!isPageDownKey) return;

          e.preventDefault();

          HANDLER.action.selection.edgeCell('down');
        },
        F6: (e: KeyboardEvent<HTMLDivElement>) => {
          const isF6Key = e.key === 'F6';
          if (!isF6Key) return;

          e.preventDefault();

          contentBarInputRef.current?.focus();
        },
        ctrl: {
          c: (e: KeyboardEvent<HTMLDivElement>) => {
            if (!isSelectionRangePresent) return;

            const isCopyShortcut = e.key === 'c' && (e.ctrlKey || e.metaKey);
            if (!isCopyShortcut) return;

            e.preventDefault();
            HANDLER.action.content.copy();
          },
          v: (e: KeyboardEvent<HTMLDivElement>) => {
            if (!isSelectionRangePresent) return;

            const isPasteShortcut = e.key === 'v' && (e.ctrlKey || e.metaKey);
            if (!isPasteShortcut) return;

            e.preventDefault();
            HANDLER.action.content.paste();
          },
          z: (e: KeyboardEvent<HTMLDivElement>) => {
            const isUndoShortcut = e.key === 'z' && (e.ctrlKey || e.metaKey);
            if (!isUndoShortcut) return;

            e.preventDefault();
            HANDLER.action.history.undo();
          },
          a: (e: KeyboardEvent<HTMLDivElement>) => {
            const isSelectAllShortcut = e.key === 'a' && (e.ctrlKey || e.metaKey);
            if (!isSelectAllShortcut) return;

            e.preventDefault();
            INTERNAL_HANDLER.selection.area({ row: 0, col: 0 }, { row: lastRow, col: lastCol });
          },
        },
        shift: {
          arrow: {
            up: (e: KeyboardEvent<HTMLDivElement>) => {
              const isShiftArrowUpKey = e.key === 'ArrowUp' && e.shiftKey;
              if (!isShiftArrowUpKey) return;

              e.preventDefault();

              HANDLER.action.selection.increase('upwards');
            },
            down: (e: KeyboardEvent<HTMLDivElement>) => {
              const isShiftArrowDownKey = e.key === 'ArrowDown' && e.shiftKey;
              if (!isShiftArrowDownKey) return;

              e.preventDefault();

              HANDLER.action.selection.increase('downwards');
            },
            left: (e: KeyboardEvent<HTMLDivElement>) => {
              const isShiftArrowLeftKey = e.key === 'ArrowLeft' && e.shiftKey;
              if (!isShiftArrowLeftKey) return;

              e.preventDefault();

              HANDLER.action.selection.increase('leftwards');
            },
            right: (e: KeyboardEvent<HTMLDivElement>) => {
              const isShiftArrowRightKey = e.key === 'ArrowRight' && e.shiftKey;
              if (!isShiftArrowRightKey) return;

              e.preventDefault();

              HANDLER.action.selection.increase('rightwards');
            },
          },
          home: (e: KeyboardEvent<HTMLDivElement>) => {
            const isShiftHomeKey = e.key === 'Home' && e.shiftKey;
            if (!isShiftHomeKey) return;

            e.preventDefault();

            HANDLER.action.selection.extendToEdgeCell('left');
          },
          end: (e: KeyboardEvent<HTMLDivElement>) => {
            const isShiftEndKey = e.key === 'End' && e.shiftKey;
            if (!isShiftEndKey) return;

            e.preventDefault();

            HANDLER.action.selection.extendToEdgeCell('right');
          },
          pageUp: (e: KeyboardEvent<HTMLDivElement>) => {
            const isShiftPageUpKey = e.key === 'PageUp' && e.shiftKey;
            if (!isShiftPageUpKey) return;

            e.preventDefault();

            HANDLER.action.selection.extendToEdgeCell('up');
          },
          pageDown: (e: KeyboardEvent<HTMLDivElement>) => {
            const isShiftPageDownKey = e.key === 'PageDown' && e.shiftKey;
            if (!isShiftPageDownKey) return;

            e.preventDefault();

            HANDLER.action.selection.extendToEdgeCell('down');
          },
        },
        ctrlShift: {
          z: (e: KeyboardEvent<HTMLDivElement>) => {
            const isRedoShortcut = e.key === 'Z' && (e.ctrlKey || e.metaKey) && e.shiftKey;
            if (!isRedoShortcut) return;

            e.preventDefault();
            HANDLER.action.history.redo();
          },
        },
      },
      cellInput: {
        tab: (
          // TODO: no se está usando este tab de momento
          e: KeyboardEvent<HTMLDivElement>
          // { values, i, options }: { values: CellMultiValue; i: number; options: CellOptions }
        ) => {
          const isTabKey = e.key === 'Tab' && !e.shiftKey && !(e.ctrlKey || e.metaKey);
          if (!isTabKey) return;

          if (isEmptyCell(targetRangeAnchorCell)) return;

          e.stopPropagation();
          e.preventDefault();

          const { row, col } = targetRangeAnchorCell;
          const { value, options } = content[row][col];

          const leftOptions = options?.filter((o) => !value.includes(o)) ?? [];
          const hasOptions = leftOptions.length > 0;
          if (!hasOptions) return;

          const hasEmptyCurrentValue = temporaryCellContent == null || temporaryCellContent === '';
          if (hasEmptyCurrentValue) return;

          const lastValueIDX = value.length - 1;

          const newValue = value.concat(['']);
          newValue[lastValueIDX] = temporaryCellContent;
          INTERNAL_HANDLER.content.draft.write('');
          HANDLER.action.content.write(targetRangeAnchorCell, newValue);
        },
        enter: (e: KeyboardEvent<HTMLDivElement>) => {
          const isEnterKey = e.key === 'Enter';
          if (!isEnterKey) return;

          e.stopPropagation();

          HANDLER.action.content.edit.confirm();
        },
        escape: (e: KeyboardEvent<HTMLDivElement>) => {
          const isEscapeKey = e.key === 'Escape';
          if (!isEscapeKey) return;

          e.stopPropagation();

          HANDLER.action.content.edit.cancel();
        },
        F6: (e: KeyboardEvent<HTMLDivElement>) => {
          const isF6Key = e.key === 'F6';
          if (!isF6Key) return;

          e.stopPropagation();
          e.preventDefault();
        },
      },
      contentBarInput: {
        tab: (
          e: KeyboardEvent<HTMLDivElement>,
          { values, i, options }: { values: CellMultiValue; i: number; options: CellOptions }
        ) => {
          const isTabKey = e.key === 'Tab' && !e.shiftKey && !(e.ctrlKey || e.metaKey);
          if (!isTabKey) return;

          e.stopPropagation();

          if (isEmptyCell(targetRangeAnchorCell)) return;

          const leftOptions = options.filter((o) => !values.includes(o));
          const hasOptions = leftOptions.length > 0;

          const currentValue = values[i];
          const hasEmptyCurrentValue = currentValue === '';
          if (hasEmptyCurrentValue || !hasOptions) {
            e.preventDefault();
            return;
          }

          const { row, col } = targetRangeAnchorCell;
          const cell = content[row][col];
          const newValue = [...cell.value].concat(['']);
          HANDLER.action.content.write(targetRangeAnchorCell, newValue);
        },
        escape: (e: KeyboardEvent<HTMLDivElement>) => {
          const isEscapeKey = e.key === 'Escape';
          if (!isEscapeKey) return;

          e.stopPropagation();

          if (isEmptyCell(targetRangeAnchorCell)) return;

          const { row, col } = targetRangeAnchorCell;
          const cell = content[row][col];
          const newValue = cell.value.filter((s) => s !== '');
          HANDLER.action.content.write(targetRangeAnchorCell, newValue);

          spreadsheetBodyRef.current?.focus();
        },
      },
    },
  };

  const convertExcelCellToInternalCellValue = (cell: ExcelJS.Cell): CellValue => {
    const { value } = cell;

    return value instanceof Date ? [dayjs.utc(value).format('YYYY-MM-DD')] : [value?.toString() ?? ''];
  };

  const workbookLoader = (workbook: ExcelJS.Workbook) => {
    const worksheet = workbook.getWorksheet(1); // TODO: ¿asumimos que trabajamos con la primera hoja?
    if (!worksheet) return;

    if (isSiloRealWorkbook) {
      worksheet.spliceRows(1, 2);
      worksheet.spliceColumns(1, 1);
    }

    const maxColumns = fixedCols ? INIT_COL : worksheet.columnCount;

    const matrix: CellValue[][] = [];
    worksheet.eachRow((row) => {
      const internalRowData = range<CellValue>(maxColumns, () => ['']);
      row.eachCell({ includeEmpty: true }, (cell, col) => {
        if (col > maxColumns) return;
        internalRowData[col - 1] = convertExcelCellToInternalCellValue(cell);
      });
      matrix.push(internalRowData);
    });

    INTERNAL_HANDLER.content.writeBulk(matrix, { row: 0, col: 0 });
    setOfferColumnMapping(false);
  };

  const [offerColumnMapping, setOfferColumnMapping] = useState(false);
  const [activeColumnMappingUI, setActiveColumnMappingUI] = useState(false);

  type ColumnMap = Map<string, number | null>;
  const [columnMapping, setColumnMapping] = useState<ColumnMap>(new Map<string, number | null>());
  const [targetWorkbookSheet, setTargetWorkbookSheet] = useState(1);

  useEffect(() => {
    if (!workbook) return;

    if (isSiloRealWorkbook) {
      workbookLoader(workbook);
      return;
    }

    setOfferColumnMapping(true);
    setActiveColumnMappingUI(false);
    setTargetWorkbookSheet(1);
    setColumnMapping(new Map());
  }, [workbook]);

  // const loadNewSheetForColumnMapping = (sheet: number) => {
  //   const worksheet = workbook?.getWorksheet(sheet);
  //   if (!worksheet) return;
  //   const columns = new Set<number>();

  //   worksheet.eachRow({ includeEmpty: false }, (row) => {
  //     row.eachCell({ includeEmpty: false }, (_, colNumber) => {
  //       columns.add(colNumber);
  //     });
  //   });

  //   setColumnMapping(new Map([...columns].map((n) => [n, null])));
  //   setTargetWorkbookSheet(sheet);
  // };

  useEffect(() => {
    if (!workbook) return;

    const mapping = new Map<string, null>();

    workbook.eachSheet((sheet, sheetID) => {
      sheet.eachRow({ includeEmpty: false }, (row) => {
        row.eachCell({ includeEmpty: false }, (_, colIDX) => {
          const key = `${sheetID}:${colIDX}`;
          mapping.set(key, null);
        });
      });
    });

    setColumnMapping(mapping);
    setTargetWorkbookSheet(1);
  }, [activeColumnMappingUI]);

  const headerRef = useRef<HTMLTableSectionElement>(null);

  useEffect(() => {
    const setOffsets = () => {
      if (headerRef.current) {
        const rows = headerRef.current.querySelectorAll('tr');
        let offset = 0;
        rows.forEach((row) => {
          row.style.position = 'sticky';
          row.style.top = offset + 'px';
          row.style.background = '#eee'; // evita solapamiento visual
          row.style.opacity = '1';
          row.style.zIndex = '5000'; // ajustalo según necesites
          offset += row.offsetHeight;
        });
      }
    };

    // Calcular offsets al montar y al redimensionar
    setOffsets();
    window.addEventListener('resize', setOffsets);
    return () => window.removeEventListener('resize', setOffsets);
  }, []);

  useImperativeHandle(ref, () => {
    return {
      action: HANDLER.action,
      state: {
        content,
        range: {
          area: targetCellRange,
          anchor: targetRangeAnchorCell,
        },
        contentHistory,
        columnResize,
        rowResize,
        workbook,
      },
      mode: {
        edit: editMode,
        selecting: selectingMode,
        autofilling: autofillingMode,
        // resizing: resizingMode,
      },
    };
  });

  // --- estilos en JS ---
  const baseBtn = {
    padding: '4px 8px',
    fontSize: 12,
    lineHeight: 1.2,
    borderRadius: 6,
    border: '1px solid #c0c4cc',
    backgroundColor: '#fff',
    color: '#1f1f1f',
    cursor: 'pointer',
    transform: 'scale(1)',
    transition: 'background-color 120ms ease, transform 80ms ease',
  };

  const hover = { backgroundColor: '#f3f4f6' };
  const pressed = { transform: 'scale(1.2)' };

  const selectedBtn = {
    ...baseBtn,
    backgroundColor: '#6baa0033',
    borderColor: '#6baa00',
    color: '#000',
  };

  const doneBtn = {
    ...baseBtn,
    backgroundColor: '#22A7F033',
    borderColor: '#22A7F0',
    padding: '4px 16px',
    color: '#000',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 32, height: '100%' }}>
      {offerColumnMapping ? (
        <div
          style={{ fontSize: 12, color: 'rgba(0, 0, 0, 0.6)', display: 'flex', gap: 8, alignItems: 'center' }}
        >
          <span>{i18n.t('app.massive_register.mapping_columns_message')}</span>
          <button
            style={baseBtn}
            onMouseEnter={(e) => Object.assign(e.currentTarget.style, hover)}
            onMouseLeave={(e) => Object.assign(e.currentTarget.style, baseBtn)}
            onClick={() => {
              setActiveColumnMappingUI(true);
              setOfferColumnMapping(false);
            }}
          >
            {i18n.t('app.yes')}
          </button>
          <button
            style={baseBtn}
            onMouseEnter={(e) => Object.assign(e.currentTarget.style, hover)}
            onMouseLeave={(e) => Object.assign(e.currentTarget.style, baseBtn)}
            onClick={() => workbookLoader(workbook!)}
          >
            {i18n.t('app.no')}
          </button>
        </div>
      ) : null}
      {activeColumnMappingUI ? (
        <div>
          <div style={{ display: 'flex', flexDirection: 'row', gap: 32, alignItems: 'center' }}>
            <div
              style={{
                display: 'flex',
                flex: 1,
                flexDirection: 'row',
                gap: 16,
                alignItems: 'center',
              }}
            >
              <div style={{ fontSize: 12, color: 'rgba(0, 0, 0, 0.6)' }}>
                {i18n.t('app.massive_register.available_pages')}
                {':'}
              </div>
              {workbook?.worksheets.map((sheet) => (
                <button
                  key={sheet.id}
                  style={sheet.id === targetWorkbookSheet ? { ...selectedBtn, ...pressed } : baseBtn}
                  onClick={() => setTargetWorkbookSheet(sheet.id)}
                  onMouseEnter={(e) =>
                    sheet.id !== targetWorkbookSheet && Object.assign(e.currentTarget.style, hover)
                  }
                  onMouseLeave={(e) =>
                    sheet.id !== targetWorkbookSheet && Object.assign(e.currentTarget.style, baseBtn)
                  }
                  onMouseDown={(e) => Object.assign(e.currentTarget.style, pressed)}
                  onMouseUp={(e) =>
                    Object.assign(
                      e.currentTarget.style,
                      sheet.id === targetWorkbookSheet
                        ? { ...selectedBtn, ...pressed }
                        : { ...baseBtn, ...hover }
                    )
                  }
                >
                  {sheet.name}
                </button>
              )) ?? null}
            </div>
            <div>
              <span style={{ fontSize: 12, color: 'rgba(0, 0, 0, 0.6)' }}>
                {i18n.t('app.massive_register.when_finish')}
                {':'}{' '}
              </span>
              <button
                style={doneBtn}
                onClick={() => {
                  setOfferColumnMapping(false);
                  setActiveColumnMappingUI(false);
                  setColumnMapping(new Map());
                }}
              >
                {i18n.t('app.massive_register.finish')}
              </button>
            </div>
          </div>
          <div
            style={{
              width: '100%',
              padding: 16,
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              maxHeight: 200,
              flexWrap: 'wrap',
            }}
          >
            {content[0].map((cell, colIDX) => {
              return (
                <div key={cell.key} style={{ display: 'flex', flexDirection: 'row', gap: 8 }}>
                  <div>
                    <select
                      style={{
                        /* tipografía y caja */
                        fontFamily: 'inherit',
                        fontSize: 12,
                        lineHeight: 1.25,
                        padding: '1px 4px',
                        width: 56,
                        height: 28,
                        cursor: 'pointer',

                        /* piel */
                        color: '#1F1F1F',
                        backgroundColor: '#FFF',
                        border: '1px solid #C0C4CC',
                        borderRadius: 4,

                        /* flecha SVG incrustada */
                        backgroundImage:
                          "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23666'><path d='M7 10l5 5 5-5'/></svg>\")",
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 2px center',
                        backgroundSize: 16,

                        /* borramos la flecha nativa */
                        appearance: 'none',
                        WebkitAppearance: 'none',
                        MozAppearance: 'none',
                      }}
                      onChange={(e) => {
                        const key = e.currentTarget.value;
                        const newColumnMapping = new Map(columnMapping);

                        const { value } = e.target as HTMLSelectElement;
                        if (value === '') {
                          // Deshacer el mapeo de la columna si el valor es '' (string vacío)
                          const col = columnMapping.entries().find(([, value]) => value === colIDX)?.[0];
                          if (col == null) return;

                          newColumnMapping.set(col, null);
                          setColumnMapping(newColumnMapping);

                          const spreadsheet = range(content.length, () => [['']]);
                          INTERNAL_HANDLER.content.writeBulk(spreadsheet, { row: 0, col: colIDX });
                          return;
                        }

                        // Desmapear la columna si ya está mapeada
                        const columnAlreadyMapped = columnMapping
                          .entries()
                          .find(([, value]) => value === colIDX);
                        if (columnAlreadyMapped) newColumnMapping.set(columnAlreadyMapped[0], null);

                        // Mapear la columna
                        newColumnMapping.set(key ?? '', colIDX);
                        setColumnMapping(newColumnMapping);

                        const targetCol = workbook
                          ?.getWorksheet(parseInt(key!.split(':')[0]))
                          ?.getColumn(parseInt(key!.split(':')[1]))
                          .values.slice(1);
                        if (!targetCol) return;

                        const colLength = Math.max(targetCol.length, content.length);

                        const spreadsheet = range(colLength, (i) => [
                          convertExcelCellToInternalCellValue({ value: targetCol[i] } as ExcelJS.Cell),
                        ]);
                        INTERNAL_HANDLER.content.writeBulk(spreadsheet, { row: 0, col: colIDX });
                      }}
                    >
                      {[['', null], ...columnMapping].map(([key, value2], i) => (
                        <option
                          key={key}
                          value={key ?? undefined}
                          style={{
                            fontSize: 12,
                            display:
                              i !== 0 &&
                              (value2 != null || parseInt(key!.split(':')[0]) !== targetWorkbookSheet)
                                ? 'none'
                                : 'block',
                          }}
                        >
                          {key !== ''
                            ? CONVERSION.column.label.fromColIDX(parseInt(key!.split(':')[1]) - 1) +
                              `(${key!.split(':')[0]})`
                            : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      backgroundColor: colIDX < 8 ? '#e5393533' : '#ffb30033',
                      border: `0px solid ${colIDX < 8 ? '#e53935' : '#ffb300'}`,
                      borderRadius: 4,
                      padding: '2px 8px',
                    }}
                  >
                    <div style={{ fontSize: 11, fontWeight: 600, color: colIDX < 8 ? '#e53935' : '#ffb300' }}>
                      {colIDX < 8 ? 'OBL.' : 'OPC.'}
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: 'rgba(0, 0, 0, 0.6)',
                        backgroundColor: 'white',
                        padding: '0 4px',
                        borderRadius: 4,
                      }}
                    >
                      {header?.[colIDX].headerName}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
      <div
        ref={spreadsheetBodyRef}
        className="siloreal-spreadsheet-container"
        style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
        tabIndex={0}
        onKeyDown={(e) => {
          // console.log('[KEY-DOWN] (from body)', e.key);

          HANDLER.shortcut.table.escape(e);
          HANDLER.shortcut.table.delete(e);
          HANDLER.shortcut.table.F6(e);

          HANDLER.shortcut.table.typing(e);

          HANDLER.shortcut.table.arrow.up(e);
          HANDLER.shortcut.table.arrow.down(e);
          HANDLER.shortcut.table.arrow.left(e);
          HANDLER.shortcut.table.arrow.right(e);
          HANDLER.shortcut.table.shift.arrow.up(e);
          HANDLER.shortcut.table.shift.arrow.down(e);
          HANDLER.shortcut.table.shift.arrow.left(e);
          HANDLER.shortcut.table.shift.arrow.right(e);

          HANDLER.shortcut.table.ctrl.a(e);
          HANDLER.shortcut.table.ctrl.c(e);
          HANDLER.shortcut.table.ctrl.v(e);
          HANDLER.shortcut.table.ctrl.z(e);
          HANDLER.shortcut.table.ctrlShift.z(e);

          HANDLER.shortcut.table.home(e);
          HANDLER.shortcut.table.end(e);
          HANDLER.shortcut.table.pageUp(e);
          HANDLER.shortcut.table.pageDown(e);
          HANDLER.shortcut.table.shift.home(e);
          HANDLER.shortcut.table.shift.end(e);
          HANDLER.shortcut.table.shift.pageUp(e);
          HANDLER.shortcut.table.shift.pageDown(e);
        }}
      >
        {/* <h1>Sheet Page</h1>
        <h2>Excel Table</h2> */}
        <div
          style={{
            display: 'flex',
            gap: 8,
            margin: 4,
            height: 28,
          }}
          onFocus={() => {
            INTERNAL_HANDLER.mode.edit.off();
            INTERNAL_HANDLER.content.draft.discard();
          }}
        >
          <span> {i18n.t('app.massive_register.content')} </span>
          {content[targetRangeAnchorCell.row ?? -1]?.[targetRangeAnchorCell.col ?? -1]?.type === 'option' ? (
            <div
              style={{
                display: 'flex',
                width: '98%',
                flexDirection: 'row',
                gap: 8,
              }}
            >
              {content[targetRangeAnchorCell.row ?? -1]?.[targetRangeAnchorCell.col ?? -1].value.map(
                (v, i, arr) => (
                  <Fragment key={i}>
                    <input
                      list="datalist"
                      disabled={
                        content[targetRangeAnchorCell.row ?? -1]?.[targetRangeAnchorCell.col ?? -1].readonly
                      }
                      ref={i === arr.length - 1 ? contentBarInputRef : undefined}
                      style={{
                        display: 'inline-block',
                        border:
                          '1px solid' +
                          (content[targetRangeAnchorCell.row ?? -1]?.[targetRangeAnchorCell.col ?? -1]
                            .feedback
                            ? content[targetRangeAnchorCell.row ?? -1]?.[targetRangeAnchorCell.col ?? -1]
                                .feedback?.type === 'error'
                              ? ' rgb(219, 35, 35)'
                              : content[targetRangeAnchorCell.row ?? -1]?.[targetRangeAnchorCell.col ?? -1]
                                  .feedback?.type === 'warning'
                              ? ' rgb(215, 197, 1)'
                              : ' black'
                            : ' black'),
                        borderRadius: 4,
                        flex: i === arr.length - 1 ? 1 : undefined,
                        backgroundColor: 'white',
                        padding: '0 8px',
                        width: 'min-content',
                      }}
                      value={v}
                      onKeyDown={(e) => {
                        e.stopPropagation();

                        const options =
                          content[targetRangeAnchorCell.row ?? -1]?.[targetRangeAnchorCell.col ?? -1]
                            ?.options ?? [];

                        const isLastInput = i === arr.length - 1;
                        if (isLastInput) HANDLER.shortcut.contentBarInput.tab(e, { values: arr, i, options });
                        HANDLER.shortcut.contentBarInput.escape(e);
                      }}
                      onChange={(e) => {
                        if (isEmptyCell(targetRangeAnchorCell)) return;

                        const newValue = arr.with(i, e.target.value);
                        HANDLER.action.content.write(targetRangeAnchorCell, newValue);
                      }}
                      onBlur={() => {
                        if (isEmptyCell(targetRangeAnchorCell)) return;

                        const currentValue = arr[i];
                        if (currentValue !== '') return;
                        const filteredValues = arr.filter((v) => v !== '');
                        const newValue = filteredValues.length ? filteredValues : [''];
                        HANDLER.action.content.write(targetRangeAnchorCell, newValue);
                      }}
                    />
                    <datalist id="datalist">
                      {content[targetRangeAnchorCell.row ?? -1]?.[targetRangeAnchorCell.col ?? -1].options
                        ?.filter((o) => !arr.includes(o))
                        .map((option, i) => <option key={option + i} value={option} />)}
                    </datalist>
                  </Fragment>
                )
              )}
            </div>
          ) : (
            <input
              ref={contentBarInputRef}
              disabled={content[targetRangeAnchorCell.row ?? -1]?.[targetRangeAnchorCell.col ?? -1].readonly}
              style={{
                display: 'inline-block',
                border: '1px solid black',
                borderRadius: 4,
                backgroundColor: 'white',
                padding: '0 8px',
                width: '98%',
              }}
              value={
                content[targetRangeAnchorCell.row ?? -1]?.[
                  targetRangeAnchorCell.col ?? -1
                ].value.toString() ?? ''
              }
              onKeyDown={(e) => {
                e.stopPropagation();
                HANDLER.shortcut.contentBarInput.escape(e);
              }}
              onChange={(e) => {
                if (isEmptyCell(targetRangeAnchorCell)) return;
                HANDLER.action.content.write(targetRangeAnchorCell, e.target.value);
              }}
            />
          )}
        </div>
        {hasErrorOnAnchorCell ? (
          <div
            style={{
              color: 'rgb(219, 35, 35)',
              display: 'flex',
              gap: 8,
              margin: 4,
              height: 28,
            }}
          >
            <strong>
              <i>Error</i>
            </strong>
            <span
              style={{
                display: 'inline-block',
                padding: '0 8px',
                width: '98%',
              }}
            >
              {content[targetRangeAnchorCell.row ?? -1]?.[targetRangeAnchorCell.col ?? -1].feedback?.message}
            </span>
          </div>
        ) : null}
        {hasWarningOnAnchorCell ? (
          <div
            style={{
              color: 'rgb(215, 197, 1)',
              display: 'flex',
              gap: 8,
              margin: 4,
              height: 28,
            }}
          >
            <strong>
              <i>Warning</i>
            </strong>
            <span
              style={{
                display: 'inline-block',
                padding: '0 8px',
                width: '98%',
              }}
            >
              {content[targetRangeAnchorCell.row ?? -1]?.[targetRangeAnchorCell.col ?? -1].feedback?.message}
            </span>
          </div>
        ) : null}
        {!hasErrorOnAnchorCell && !hasWarningOnAnchorCell ? (
          <div
            style={{
              display: 'flex',
              gap: 8,
              margin: 4,
              height: 28,
            }}
          >
            <span> </span>
          </div>
        ) : null}

        <div style={{ minHeight: 0, height: '100%', overflow: 'auto' }}>
          <table style={{ tableLayout: 'fixed' }}>
            <thead ref={headerRef} className="table-header">
              <tr>
                <th></th>
                {header?.slice(0, lastCol + 1).map((col, i) => (
                  <th
                    key={col.headerName}
                    style={{
                      display: columnResize[i]?.offset === 0 ? 'none' : undefined,
                      width: columnResize[i]?.offset ?? 128,
                      height: 20,
                      // backgroundColor: content[rowIDX][colIDX].readonly ? '#dedede' : 'white',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        width: columnResize[i]?.offset ?? 128,
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {col.headerName}
                    </div>
                  </th>
                ))}
              </tr>
              <tr>
                <th style={{ position: 'sticky', zIndex: 5000 }}>#</th>
                {content[0].map((cell, colIDX) => {
                  const isColInSelectionRange =
                    isSelectionRangePresent &&
                    colIDX >= targetCellRange.start.col! &&
                    colIDX <= targetCellRange.end.col!;

                  return (
                    <Fragment key={cell.key}>
                      {columnResize[colIDX]?.offset ?? 128 ? (
                        <th
                          key={colIDX}
                          className="table-column-header table-cell"
                          style={{
                            width: columnResize[colIDX]?.offset ?? 128,
                            backgroundColor: isColInSelectionRange ? '#D7E8BA' : undefined,
                          }}
                          onClick={() => HANDLER.action.selection.column(colIDX)}
                        >
                          <span
                            style={{
                              position: 'absolute',
                              bottom: '-1px',
                              left: '-1px',
                              width: (columnResize[colIDX]?.offset ?? 128) + 1,
                              borderBottom: isColInSelectionRange ? '2px solid #6baa00' : undefined,
                              zIndex: 1000,
                            }}
                          />
                          <div className="col-header-container">
                            <div className="col-header-content">
                              <span>{CONVERSION.column.label.fromColIDX(colIDX)}</span>
                            </div>
                            <span
                              className="col-header-border-slider"
                              draggable
                              onDragStart={(e) => HANDLER.action.resize.column.start(colIDX, e.clientX)}
                              onDragEnd={(e) => HANDLER.action.resize.column.end(colIDX, e.clientX)}
                              onDoubleClick={() => HANDLER.action.resize.column.restore(colIDX)}
                            />
                          </div>
                        </th>
                      ) : (
                        <th
                          key={colIDX}
                          className="col-collapse"
                          style={{ border: 'transparent' }}
                          onClick={() => HANDLER.action.resize.column.restore(colIDX)}
                        >
                          <span>...</span>
                        </th>
                      )}
                    </Fragment>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {content.map((row, rowIDX) => {
                const isRowInSelectionRange =
                  isSelectionRangePresent &&
                  rowIDX >= targetCellRange.start.row! &&
                  rowIDX <= targetCellRange.end.row!;

                return (
                  <tr key={row[0].key} className="table-row">
                    {rowResize[rowIDX]?.offset ?? 20 ? (
                      <>
                        <td
                          className="table-header table-row-header table-cell"
                          style={{
                            height: rowResize[rowIDX]?.offset ?? 20,
                            backgroundColor: isRowInSelectionRange ? '#D7E8BA' : undefined,
                            position: 'sticky',
                            left: 0,
                            zIndex: 20000,
                          }}
                          onClick={() => HANDLER.action.selection.row(rowIDX)}
                        >
                          <span
                            style={{
                              position: 'absolute',
                              top: '-1px',
                              right: '-1px',
                              height: (rowResize[rowIDX]?.offset ?? 20) + 2,
                              borderRight: isRowInSelectionRange ? '2px solid #6baa00' : undefined,
                            }}
                          />
                          <div className="row-header-container">
                            <div className="row-header-content">{rowIDX + 1}</div>
                            <span
                              className="row-header-border-slider"
                              draggable
                              onDragStart={(e) => HANDLER.action.resize.row.start(rowIDX, e.clientY)}
                              onDragEnd={(e) => HANDLER.action.resize.row.end(rowIDX, e.clientY)}
                              onDoubleClick={() => HANDLER.action.resize.row.restore(rowIDX)}
                            />
                          </div>
                        </td>
                        {content[rowIDX].map((cell, colIDX) => {
                          return (
                            <td
                              key={`${row[0].key}-${cell.key}`}
                              ref={(node) => {
                                const isAnchorCell =
                                  targetRangeAnchorCell.row === rowIDX &&
                                  targetRangeAnchorCell.col === colIDX;
                                if (isAnchorCell) {
                                  node?.scrollIntoView({
                                    behavior: 'smooth',
                                    inline: 'center',
                                    block: 'center',
                                  });
                                }
                              }}
                              className="table-cell table-inner-cell"
                              style={{
                                margin: 0,
                                padding: 0,
                                cursor: selectingMode ? 'cell' : undefined,
                                height: rowResize[rowIDX]?.offset ?? 20,
                                backgroundColor:
                                  columnResize[colIDX]?.offset === 0 ? 'transparent' : undefined,
                                border: columnResize[colIDX]?.offset === 0 ? 'none' : undefined,
                              }}
                              onDragEnter={() =>
                                autofillingMode
                                  ? HANDLER.action.selection.autocomplete.trackCellTraverse({
                                      row: rowIDX,
                                      col: colIDX,
                                    })
                                  : HANDLER.action.selection.drag.trackCellTraverse({
                                      row: rowIDX,
                                      col: colIDX,
                                    })
                              }
                            >
                              {(columnResize[colIDX]?.offset ?? 128) &&
                              ((autofillingMode &&
                                targetRangeAnchorCell.row === rowIDX &&
                                targetRangeAnchorCell.col === colIDX) ||
                                (!autofillingMode &&
                                  targetCellRange.end.row === rowIDX &&
                                  targetCellRange.end.col === colIDX)) ? (
                                <span
                                  key="auto-fill-dot"
                                  draggable
                                  style={{
                                    position: 'absolute',
                                    bottom: -4.5,
                                    right: -4.5,
                                    backgroundColor: '#6baa00',
                                    width: 8,
                                    height: 8,
                                    border: '1px solid white',
                                    borderRadius: 1,
                                    margin: 0,
                                    padding: 0,
                                    cursor: 'pointer',
                                    zIndex: 1500,
                                  }}
                                  onDragStart={() =>
                                    HANDLER.action.selection.autocomplete.start({ row: rowIDX, col: colIDX })
                                  }
                                  onDragEnd={() => HANDLER.action.selection.autocomplete.end()}
                                />
                              ) : null}
                              <div
                                onMouseDown={(e) => {
                                  if (e.button !== 0) return;
                                  HANDLER.action.selection.cell({ row: rowIDX, col: colIDX });
                                }}
                                draggable={!editMode}
                                onDragStart={(e) => {
                                  e.dataTransfer.setDragImage(new Image(), 0, 0);
                                  HANDLER.action.selection.drag.start({ row: rowIDX, col: colIDX });
                                }}
                                onDragEnd={() => {
                                  HANDLER.action.selection.drag.end();
                                }}
                                onDoubleClick={() => {
                                  HANDLER.action.content.edit.start({ row: rowIDX, col: colIDX }, 'soft');
                                }}
                              >
                                {columnResize[colIDX]?.offset ?? 128 ? (
                                  <span
                                    key="selected-cell"
                                    style={{
                                      position: 'absolute',
                                      margin: 0,
                                      padding: 0,
                                      top: '-2px',
                                      left: '-2px',
                                      width: (columnResize[colIDX]?.offset ?? 128) + 4,
                                      height: (rowResize[rowIDX]?.offset ?? 20) + 4,
                                      borderRadius: 1,
                                      zIndex: 1000,
                                      pointerEvents: 'none',
                                      ...calculateSelectedCellStyles({
                                        x: colIDX,
                                        y: rowIDX,
                                        targetCellRange,
                                        anchorCell: targetRangeAnchorCell as DefinedCellIDX,
                                      }),
                                    }}
                                  />
                                ) : null}
                                {/* WORKING HERE */}
                                {targetCellRange.start.row === rowIDX &&
                                targetCellRange.start.col === colIDX &&
                                editMode ? (
                                  <div
                                    className="input-sizer"
                                    data-value={temporaryCellContent ?? content[rowIDX][colIDX]}
                                    style={{
                                      display: columnResize[colIDX]?.offset === 0 ? 'none' : undefined,
                                      height: rowResize[rowIDX]?.offset ?? 20,
                                      minWidth: columnResize[colIDX]?.offset ?? 128,
                                    }}
                                  >
                                    <input
                                      autoFocus
                                      list="datalist"
                                      value={temporaryCellContent ?? content[rowIDX][colIDX].value.at(1)}
                                      onInput={(e) => {
                                        e.stopPropagation();
                                        HANDLER.action.content.edit.typing(e.currentTarget.value);
                                      }}
                                      onMouseDown={(e) => e.stopPropagation()}
                                      onKeyDown={(e) => {
                                        e.stopPropagation();
                                        // console.log('[KEY-DOWN] (from editable content)', e.key);

                                        HANDLER.shortcut.cellInput.tab(e);
                                        HANDLER.shortcut.cellInput.enter(e);
                                        HANDLER.shortcut.cellInput.escape(e);
                                        HANDLER.shortcut.cellInput.F6(e);
                                      }}
                                      style={{
                                        display: columnResize[colIDX]?.offset === 0 ? 'none' : undefined,
                                        height: rowResize[rowIDX]?.offset ?? 20,
                                      }}
                                    />
                                    <datalist id="datalist">
                                      {content[rowIDX][colIDX].options?.map((option, i) => (
                                        <option key={i} value={option}></option>
                                      ))}
                                    </datalist>
                                  </div>
                                ) : (
                                  <div
                                    className="cell-input"
                                    style={{
                                      display: columnResize[colIDX]?.offset === 0 ? 'none' : undefined,
                                      width: columnResize[colIDX]?.offset ?? 128,
                                      height: rowResize[rowIDX]?.offset ?? 20,
                                      backgroundColor: content[rowIDX][colIDX].readonly ? '#dedede' : 'white',
                                      overflow: 'hidden',
                                    }}
                                  >
                                    {content[rowIDX][colIDX].value.toString()}
                                  </div>
                                )}
                                {content[rowIDX][colIDX].feedback?.type === 'error' ? (
                                  <>
                                    <span
                                      key="error-cell"
                                      style={{
                                        position: 'absolute',
                                        margin: 0,
                                        padding: 0,
                                        top: 0,
                                        left: 0,
                                        width: columnResize[colIDX]?.offset ?? 128,
                                        height: rowResize[rowIDX]?.offset ?? 20,
                                        border: '2px solid rgb(238, 116, 116)',
                                        borderRadius: 0,
                                        zIndex: 500,
                                        pointerEvents: 'none',
                                      }}
                                    />
                                    <img
                                      style={{
                                        position: 'absolute',
                                        top: -7,
                                        right: 4,
                                        width: 12,
                                        height: 12,
                                        zIndex: 4000,
                                      }}
                                      src={ErrorCellIcon}
                                    />
                                  </>
                                ) : null}
                                {content[rowIDX][colIDX].feedback?.type === 'warning' ? (
                                  <>
                                    <span
                                      key="error-cell"
                                      style={{
                                        position: 'absolute',
                                        margin: 0,
                                        padding: 0,
                                        top: 0,
                                        left: 0,
                                        width: columnResize[colIDX]?.offset ?? 128,
                                        height: rowResize[rowIDX]?.offset ?? 20,
                                        border: '2px solid #ffea00',
                                        borderRadius: 0,
                                        zIndex: 500,
                                        pointerEvents: 'none',
                                      }}
                                    />
                                    <img
                                      style={{
                                        position: 'absolute',
                                        top: -7,
                                        right: 4,
                                        width: 14,
                                        height: 14,
                                        zIndex: 4000,
                                      }}
                                      src={WarningCellIcon}
                                    />
                                  </>
                                ) : null}
                              </div>
                            </td>
                          );
                        })}
                      </>
                    ) : (
                      <td
                        key={rowIDX}
                        className="row-collapse"
                        onClick={() => {
                          setRowResize((prev) => {
                            const prevConfig = prev[rowIDX] ?? {};

                            const newRowResizeConfig = { ...prevConfig, offset: 20 };
                            const newRowResize = [...prev];
                            newRowResize[rowIDX] = newRowResizeConfig;
                            return newRowResize;
                          });
                        }}
                      >
                        <span>...</span>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

type BorderCalculationParams = {
  x: number;
  y: number;
  targetCellRange: SelectionRange;
  anchorCell: SelectionRangeCorner;
};
function calculateSelectedCellStyles({ x, y, targetCellRange, anchorCell }: BorderCalculationParams) {
  const xStart = targetCellRange.start.col;
  const yStart = targetCellRange.start.row;
  const xEnd = targetCellRange.end.col;
  const yEnd = targetCellRange.end.row;

  if (xStart == null) return;
  if (yStart == null) return;
  if (xEnd == null) return;
  if (yEnd == null) return;

  const isCellInSelectionRange = y >= yStart && y <= yEnd && x >= xStart && x <= xEnd;
  if (!isCellInSelectionRange) return {};

  const isAnchorCell = x === anchorCell.col && y === anchorCell.row;
  let styles: Record<string, string> = { background: isAnchorCell ? 'transparent' : '#6baa001A' };

  const isCellAtTop = y === yStart;
  const isCellAtBottom = y === yEnd;
  const isCellAtLeft = x === xStart;
  const isCellAtRight = x === xEnd;

  if (isCellAtTop) styles = { ...styles, borderTop: '2px solid #6baa00' };
  if (isCellAtBottom) styles = { ...styles, borderBottom: '2px solid #6baa00' };
  if (isCellAtLeft) styles = { ...styles, borderLeft: '2px solid #6baa00' };
  if (isCellAtRight) styles = { ...styles, borderRight: '2px solid #6baa00' };

  return styles;
}

type NewRangeStateCalculationParams = {
  anchorCell: SelectionRangeCorner | EmptyCellIDX;
  newRange: SelectionRangeCorner;
};
function calculateNewRangeState({ anchorCell, newRange }: NewRangeStateCalculationParams): SelectionRange {
  // From now on everything is always thought from the perspective
  // of where is the new range corner relative to the previous range start corner

  const anchor = { row: anchorCell.row ?? 0, col: anchorCell.col ?? 0 };

  const isSecondQuarter = newRange.col <= anchor.col && newRange.row <= anchor.row;
  if (isSecondQuarter) return { start: newRange, end: anchor };

  const isFourthQuarter = newRange.col >= anchor.col && newRange.row >= anchor.row;
  if (isFourthQuarter) return { start: anchor, end: newRange };

  const isThirdQuarter = newRange.col < anchor.col && newRange.row > anchor.row;
  if (isThirdQuarter) {
    const newRangeStart = { row: anchor.row, col: newRange.col };
    const newRangeEnd = { row: newRange.row, col: anchor.col };
    return { start: newRangeStart, end: newRangeEnd };
  }

  const isFirstQuarter = newRange.col > anchor.col && newRange.row < anchor.row;
  if (isFirstQuarter) {
    const newRangeStart = { row: newRange.row, col: anchor.col };
    const newRangeEnd = { row: anchor.row, col: newRange.col };
    return { start: newRangeStart, end: newRangeEnd };
  }

  return { start: newRange, end: newRange }; // just added for the sake of TypeScript, this return isn't necessary
}
function calculateNewShrinkedRangeState({
  anchorCell,
  newRange,
}: NewRangeStateCalculationParams): SelectionRange {
  const anchor = { row: anchorCell.row ?? 0, col: anchorCell.col ?? 0 };

  const rowAbs = Math.abs(newRange.row - anchor.row);
  const colAbs = Math.abs(newRange.col - anchor.col);
  const isColGreater = colAbs > rowAbs;

  if (isColGreater) {
    if (newRange.col < anchor.col) {
      const newRangeStart = { row: anchor.row, col: newRange.col };
      const newRangeEnd = { row: anchor.row, col: anchor.col };
      return { start: newRangeStart, end: newRangeEnd };
    }

    if (newRange.col > anchor.col) {
      const newRangeStart = { row: anchor.row, col: anchor.col };
      const newRangeEnd = { row: anchor.row, col: newRange.col };
      return { start: newRangeStart, end: newRangeEnd };
    }
  }

  if (!isColGreater) {
    if (newRange.row < anchor.row) {
      const newRangeStart = { row: newRange.row, col: anchor.col };
      const newRangeEnd = { row: anchor.row, col: anchor.col };
      return { start: newRangeStart, end: newRangeEnd };
    }

    if (newRange.row > anchor.row) {
      const newRangeStart = { row: anchor.row, col: anchor.col };
      const newRangeEnd = { row: newRange.row, col: anchor.col };
      return { start: newRangeStart, end: newRangeEnd };
    }
  }

  return { start: newRange, end: newRange }; // just added for the sake of TypeScript, this return isn't necessary
}

function range<T = number>(length: number, cb?: (idx: number) => T): T[] {
  return Array.from({ length }, (_, i) => (cb ? cb(i) : (i as T)));
}

function initializeCell(
  cellIDX: DefinedCellIDX,
  config: {
    value?: CellValue;
    options: OptionTracker;
    readonly: ReadonlyTracker;
    validation: ValidationTracker;
  }
) {
  const cellOptions = EXTRACT.tracked.options.forCellIDX(cellIDX, config.options);
  const cellReadonly = EXTRACT.tracked.readonly.forCellIDX(cellIDX, config.readonly);
  const cellValidation = EXTRACT.tracked.validation.forCellIDX(cellIDX, config.validation);

  // console.group('initializeCell', cellIDX);
  // console.log({ cellOptions, cellReadonly, cellValidation });
  // console.groupEnd();

  const isChoiceCell = cellOptions != null;

  return {
    key: crypto.randomUUID(),
    type: isChoiceCell ? 'option' : 'text',
    value: config.value ?? [''],
    options: cellOptions,
    readonly: cellReadonly,
    validation: cellValidation,
    feedback: null,
  } as Cell;
}

function isAvailableSelectionRange(range: SelectionRange): range is AvailableSelectionRange {
  const { start, end } = range;
  const { row: rowStart, col: colStart } = start;
  const { row: rowEnd, col: colEnd } = end;

  const isStartRowAvailable = rowStart != null;
  const isStartColAvailable = colStart != null;
  const isEndRowAvailable = rowEnd != null;
  const isEndColAvailable = colEnd != null;

  return isStartRowAvailable && isStartColAvailable && isEndRowAvailable && isEndColAvailable;
}

function isEmptyCell(cell: DefinedCellIDX | EmptyCellIDX): cell is EmptyCellIDX {
  return cell.row == null || cell.col == null;
}

/*
<h3> Estados Internos </h3>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            gap: '64px',
          }}
        >
          <div>
            <h4> Column Resize </h4>
            <pre
              style={{
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                overflow: 'auto',
                border: '1px solid #ccc',
                borderRadius: '4px',
                padding: '8px',
                color: '#333',
                fontSize: '12px',
                lineHeight: '1.5',
                fontFamily: 'monospace',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              {JSON.stringify(columnResize, null, 2)}
            </pre>
          </div>
          <div>
            <h4> Row Resize </h4>
            <pre
              style={{
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                overflow: 'auto',
                border: '1px solid #ccc',
                borderRadius: '4px',
                padding: '8px',
                color: '#333',
                fontSize: '12px',
                lineHeight: '1.5',
                fontFamily: 'monospace',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              {JSON.stringify(rowResize, null, 2)}
            </pre>
          </div>
          <div>
            <h4> Target Cell Range </h4>
            <pre
              style={{
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                overflow: 'auto',
                border: '1px solid #ccc',
                borderRadius: '4px',
                padding: '8px',
                color: '#333',
                fontSize: '12px',
                lineHeight: '1.5',
                fontFamily: 'monospace',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              {JSON.stringify(targetCellRange, null, 2)}
            </pre>
          </div>
          <div>
            <h4> Edit Mode </h4>
            <pre
              style={{
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                overflow: 'auto',
                border: '1px solid #ccc',
                borderRadius: '4px',
                padding: '8px',
                color: '#333',
                fontSize: '12px',
                lineHeight: '1.5',
                fontFamily: 'monospace',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <span>{JSON.stringify(editMode, null, 2)}</span>
              <span
                style={{
                  display: 'inline-block',
                  width: '16px',
                  height: '16px',
                  backgroundColor: editMode ? '#8bc458' : '#ff4a5d',
                }}
              />
            </pre>
          </div>
          <div>
            <h4> Selecting Mode </h4>
            <pre
              style={{
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                overflow: 'auto',
                border: '1px solid #ccc',
                borderRadius: '4px',
                padding: '8px',
                color: '#333',
                fontSize: '12px',
                lineHeight: '1.5',
                fontFamily: 'monospace',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <span>{JSON.stringify(selectingMode, null, 2)}</span>
              <span
                style={{
                  display: 'inline-block',
                  width: '16px',
                  height: '16px',
                  backgroundColor: selectingMode ? '#8bc458' : '#ff4a5d',
                }}
              />
            </pre>
            <pre
              style={{
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                overflow: 'auto',
                border: '1px solid #ccc',
                borderRadius: '4px',
                padding: '8px',
                color: '#333',
                fontSize: '12px',
                lineHeight: '1.5',
                fontFamily: 'monospace',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              {JSON.stringify(targetRangeAnchorCell, null, 2)}
            </pre>
          </div>
          <div>
            <h4> Autofilling Mode </h4>
            <pre
              style={{
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                overflow: 'auto',
                border: '1px solid #ccc',
                borderRadius: '4px',
                padding: '8px',
                color: '#333',
                fontSize: '12px',
                lineHeight: '1.5',
                fontFamily: 'monospace',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <span>{JSON.stringify(autofillingMode, null, 2)}</span>
              <span
                style={{
                  display: 'inline-block',
                  width: '16px',
                  height: '16px',
                  backgroundColor: autofillingMode ? '#8bc458' : '#ff4a5d',
                }}
              />
            </pre>
          </div>
        </div>
*/
